import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/firebase/api-client";
import { Plus, Edit2, Trash2, Tag, Palette, Hash, ArrowLeft } from "lucide-react";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { AlertModal } from "@/components/shared/AlertModal";
import { useModal } from "@/hooks/useModal";
import Loading from "@/components/shared/Loading";

interface Category {
  id: string;
  name: string;
  code: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  order: number;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    icon: "",
    color: "#10b981",
    order: 0,
    isActive: true,
    // Pricing
    taux: 0.82, // Par défaut 0.82 (18% de frais)
    packagingPrice: 1.5, // Par défaut 1.5 DH
  });

  // Modales
  const deleteModal = useModal();
  const successModal = useModal();
  const errorModal = useModal();
  const [deleteCategory, setDeleteCategory] = useState<{ id: string; name: string } | null>(null);
  const [modalMessage, setModalMessage] = useState({ title: "", message: "" });

  // Charger les catégories
  useEffect(() => {
    loadCategories();
  }, []);

  async function loadCategories() {
    try {
      setLoading(true);
      const data = await api.categories.getAll();
      setCategories(data || []);
    } catch (error) {
      console.error("Error loading categories:", error);
      setModalMessage({
        title: "Erreur",
        message: "Erreur lors du chargement des catégories",
      });
      errorModal.open();
    } finally {
      setLoading(false);
    }
  }

  // Réinitialiser le formulaire
  function resetForm() {
    setFormData({
      name: "",
      code: "",
      description: "",
      icon: "",
      color: "#10b981",
      order: 0,
      isActive: true,
      taux: 0.82,
      packagingPrice: 1.5,
    });
    setEditingCategory(null);
    setShowForm(false);
  }

  // Ouvrir formulaire de création
  function handleCreate() {
    resetForm();
    setShowForm(true);
  }

  // Ouvrir formulaire d'édition
  function handleEdit(category: Category) {
    setFormData({
      name: category.name,
      code: category.code,
      description: category.description || "",
      icon: category.icon || "",
      color: category.color || "#10b981",
      order: category.order || 0,
      isActive: category.isActive,
      taux: (category as any).taux || 0.82,
      packagingPrice: (category as any).packagingPrice || 1.5,
    });
    setEditingCategory(category);
    setShowForm(true);
  }

  // Soumettre le formulaire
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.categories.update(editingCategory.id, formData);
        setModalMessage({
          title: "Succès",
          message: "Catégorie modifiée avec succès",
        });
        successModal.open();
      } else {
        await api.categories.create(formData);
        setModalMessage({
          title: "Succès",
          message: "Catégorie créée avec succès",
        });
        successModal.open();
      }
      resetForm();
      loadCategories();
    } catch (error: any) {
      console.error("Error saving category:", error);
      setModalMessage({
        title: "Erreur",
        message: error.message || "Erreur lors de la sauvegarde",
      });
      errorModal.open();
    }
  }

  // Ouvrir modal de suppression
  function handleDeleteClick(category: Category) {
    setDeleteCategory({ id: category.id, name: category.name });
    deleteModal.open();
  }

  // Confirmer suppression (désactivation)
  async function handleDeleteConfirm() {
    if (!deleteCategory) return;

    try {
      await api.categories.delete(deleteCategory.id);
      deleteModal.close();
      setModalMessage({
        title: "Succès",
        message: "Catégorie désactivée avec succès",
      });
      successModal.open();
      loadCategories();
    } catch (error: any) {
      console.error("Error deleting category:", error);
      deleteModal.close();
      setModalMessage({
        title: "Erreur",
        message: error.message || "Erreur lors de la désactivation",
      });
      errorModal.open();
    }
  }

  // Générer le code automatiquement depuis le nom
  function generateCodeFromName(name: string) {
    return name
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Supprimer les accents
      .replace(/[^A-Z0-9]/g, "_") // Remplacer les caractères spéciaux par _
      .replace(/_+/g, "_") // Remplacer les _ multiples par un seul
      .replace(/^_|_$/g, ""); // Supprimer les _ au début et à la fin
  }

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <Loading />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 md:mb-6">
        <Link
          to="/admin/inventory"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900 text-sm font-medium transition-colors duration-200 border border-gray-200 hover:border-gray-300"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour à l'Inventaire</span>
        </Link>
      </div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold">Gestion des Catégories</h1>
        <button
          onClick={handleCreate}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 flex items-center justify-center gap-2 min-h-[44px] w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          <span>Nouvelle Catégorie</span>
        </button>
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="bg-white p-4 md:p-6 rounded-lg shadow mb-4 md:mb-6">
          <h2 className="text-lg md:text-xl font-semibold mb-4">
            {editingCategory ? "Modifier la Catégorie" : "Nouvelle Catégorie"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      name: e.target.value,
                      code: editingCategory ? formData.code : generateCodeFromName(e.target.value),
                    });
                  }}
                  className="w-full px-3 py-2 border rounded min-h-[44px]"
                  placeholder="Ex: Viande"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  Code *
                </label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""),
                    })
                  }
                  className="w-full px-3 py-2 border rounded min-h-[44px] font-mono"
                  placeholder="Ex: VIANDE"
                  pattern="^[A-Z0-9_]+$"
                  title="Code en majuscules avec underscores uniquement"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Majuscules et underscores uniquement
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border rounded min-h-[80px]"
                placeholder="Description optionnelle de la catégorie"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Icône (nom Lucide)
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 border rounded min-h-[44px]"
                  placeholder="Ex: Beef"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Nom de l'icône Lucide React
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Couleur
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-16 h-10 border rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="flex-1 px-3 py-2 border rounded min-h-[44px] font-mono text-sm"
                    placeholder="#10b981"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Ordre d'affichage
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.order}
                  onChange={(e) =>
                    setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
                  }
                  className="w-full px-3 py-2 border rounded min-h-[44px]"
                />
              </div>
            </div>

            <div className="flex items-center min-h-[44px]">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="mr-2 w-5 h-5"
              />
              <label htmlFor="isActive" className="text-sm font-medium">
                Catégorie active
              </label>
            </div>

            {/* Section Pricing */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-lg font-semibold mb-4">Configuration du Pricing</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Taux de catégorie *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    required
                    value={formData.taux}
                    onChange={(e) =>
                      setFormData({ ...formData, taux: parseFloat(e.target.value) || 0.82 })
                    }
                    className="w-full px-3 py-2 border rounded min-h-[44px]"
                    placeholder="0.82"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Ex: 0.82 = 82% de revenu (18% de frais/commission)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Coût d'emballage (DH) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={formData.packagingPrice}
                    onChange={(e) =>
                      setFormData({ ...formData, packagingPrice: parseFloat(e.target.value) || 1.5 })
                    }
                    className="w-full px-3 py-2 border rounded min-h-[44px]"
                    placeholder="1.5"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Coût d'emballage en dirhams
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t">
              <button
                type="submit"
                className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 min-h-[44px] font-medium"
              >
                {editingCategory ? "Modifier" : "Créer"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 min-h-[44px] font-medium"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des catégories - Mobile: Cards */}
      <div className="md:hidden space-y-4">
        {categories.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
            <Tag className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>Aucune catégorie trouvée</p>
          </div>
        ) : (
          categories.map((category) => (
            <div key={category.id} className="bg-white p-4 rounded-lg shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {category.color && (
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                    )}
                    <h3 className="font-semibold text-lg">{category.name}</h3>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Hash className="w-4 h-4 flex-shrink-0" />
                      <span className="font-mono">{category.code}</span>
                    </div>
                    {category.description && (
                      <p className="text-xs">{category.description}</p>
                    )}
                    {category.icon && (
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 flex-shrink-0" />
                        <span>Icône: {category.icon}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold">Taux:</span>
                      <span className="text-xs">
                        {(category as any).taux !== undefined ? (category as any).taux : "Non configuré"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold">Emballage:</span>
                      <span className="text-xs">
                        {(category as any).packagingPrice !== undefined
                          ? `${(category as any).packagingPrice} DH`
                          : "Non configuré"}
                      </span>
                    </div>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs flex-shrink-0 ${
                    category.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {category.isActive ? "Actif" : "Inactif"}
                </span>
              </div>
              <div className="flex gap-2 pt-3 border-t">
                <button
                  onClick={() => handleEdit(category)}
                  className="flex-1 flex items-center justify-center gap-2 text-blue-600 hover:text-blue-800 text-sm py-2 border border-blue-600 rounded min-h-[44px] font-medium"
                >
                  <Edit2 className="w-4 h-4" />
                  Modifier
                </button>
                <button
                  onClick={() => handleDeleteClick(category)}
                  className="flex-1 flex items-center justify-center gap-2 text-red-600 hover:text-red-800 text-sm py-2 border border-red-600 rounded min-h-[44px] font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  {category.isActive ? "Désactiver" : "Supprimer"}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop: Table */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Nom</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Code</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Taux</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Emballage</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Ordre</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Aucune catégorie trouvée
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {category.color && (
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                        )}
                        <span className="font-medium">{category.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm">{category.code}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(category as any).taux ? (category as any).taux : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(category as any).packagingPrice ? `${(category as any).packagingPrice} DH` : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">{category.order}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          category.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {category.isActive ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(category)}
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        >
                          <Edit2 className="w-4 h-4" />
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDeleteClick(category)}
                          className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                        >
                          <Trash2 className="w-4 h-4" />
                          {category.isActive ? "Désactiver" : "Supprimer"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modales */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDeleteConfirm}
        title="Désactiver la Catégorie"
        message={`Êtes-vous sûr de vouloir désactiver la catégorie "${deleteCategory?.name}" ? Elle ne sera plus disponible pour les nouveaux produits et fournisseurs.`}
        type="warning"
        confirmText="Désactiver"
      />

      <AlertModal
        isOpen={successModal.isOpen}
        onClose={successModal.close}
        title={modalMessage.title}
        message={modalMessage.message}
        type="success"
      />

      <AlertModal
        isOpen={errorModal.isOpen}
        onClose={errorModal.close}
        title={modalMessage.title}
        message={modalMessage.message}
        type="error"
      />
    </div>
  );
}

